/*
  NewPassword.js - Customer Email Verification Page

  Takes a unique link parameter, verifies the validity of it, and produced
  a form for filling out if all looks good.
*/

// NPM MODULES
import React, { useEffect, useState } from 'react';
import {
  TextField,
  Stack,
  Box,
  IconButton,
  Alert,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// OUR COMPONENTS
import LoaderButton from '../../components/common/LoaderButton';
import FullscreenLayout from '../../components/layouts/FullscreenLayout';
import BackButton from '../../components/common/BackButton';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import Loader from '../../components/common/Loader';

// I18N
import PasswordRecoveryFormattedMessages from '../../messages/PasswordRecoveryFormattedMessages';

// IMGS
import CompleteSMSLogo from '../../images/logo-CompleteSMS.svg';

// NETWORKING
import useRecoverPasswordRequest from '../../requests/RecoverPasswordRequest';
import useLoginRequest from '../../requests/LoginRequest';

import { validatePassword } from '../../utils/validate';

const NewPassword = (props) => {
  const [isLoading, setLoading] = useState(true);
  const [isCompleted, setCompleted] = useState(false);
  const [hasError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [validUrl, setValidUrl] = useState(false);
  const [email, setEmail] = useState('');
  const [validPassword, setValidPassword] = useState(true);
  const [validConfirmPassword, setValidConfirmPassword] = useState(true);
  const { login } = useLoginRequest();
  const { verifyURL, setNewPassword } = useRecoverPasswordRequest();

  const [state, setState] = useState({
    newPassword: '',
    password: '',
    showPassword: false,
  });

  const { uniqueUrl } = useParams();

  useEffect(() => {
    async function getData() {
      try {
        if (!uniqueUrl || typeof uniqueUrl === 'undefined') {
          setLoading(false);
          setError(true);
          setErrorMessage('No Unique Link Found in URL');
        } else {
          let validatedUrlResult = await verifyURL(
            uniqueUrl,
            props.errorHandler
          );

          // Validate unique url
          if (validatedUrlResult.valid) {
            setLoading(false);
            setValidUrl(true);
            setEmail(validatedUrlResult.email);
          }
          // Invalid Unique Link provided
          else {
            setLoading(false);
            setError(true);
            setErrorMessage(validatedUrlResult.error || 'Invalid Unique Link');
          }
        }
      } catch (e) {
        // Network issues
        setLoading(false);
        setError(true);
        setErrorMessage('Network Connectivity Issue');
      }
    }

    getData();
  }, [props.errorHandler, uniqueUrl]);

  const handleLogin = async (emailLogin, password) => {
    try {
      // Make network Req for Login
      let result = await login(emailLogin, password, props.errorHandler);

      // Handle login failure
      if (!result || typeof result === 'undefined') {
        setLoading(false);
        setError(true);
        setErrorMessage('Incorrect Login Details');
        setCompleted(false);
        return;
      }

      localStorage.setItem('rox_user_token', 'exists');
      localStorage.setItem('rox_user_email', emailLogin);
      // send authenticated state back to App.js
      props.userHasAuthenticated(true, result);

      // change page
      props.navigate('/home');
    } catch (e) {
      setLoading(false);
      setError(true);
      setErrorMessage('Network Error');
      setCompleted(false);
    }
  };

  /*
      Ensure we have some values before sending form
    */
  const validateForm = () => {
    return state.password !== '' && state.newPassword !== '';
  };

  /*
        Handles changes in the form
    */
  const handleChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    setState({
      ...state,
      [name]: value,
    });
  };

  // useEffect hook for updating validPassword and validConfirmPassword.
  useEffect(() => {
    if (!state.password || state.password === '') {
      setValidPassword(true);
    } else {
      if (validatePassword(state.password)) {
        setValidPassword(true);
      } else {
        setValidPassword(false);
      }
    }
    if (state.newPassword && state.newPassword !== state.password) {
      setValidConfirmPassword(false);
    } else {
      setValidConfirmPassword(true);
    }
  }, [state]);

  /*
      Handles form submission
    */
  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);
    setError(false);

    try {
      if (state.password && state.password !== '') {
        if (!validatePassword(state.password)) {
          setError(true);
          setLoading(false);
          setErrorMessage(
            PasswordRecoveryFormattedMessages.errCheckPasswordStrong
          );
          return;
        } else if (state.password !== state.newPassword) {
          setError(true);
          setLoading(false);
          setErrorMessage(
            PasswordRecoveryFormattedMessages.errCheckPasswordMatch
          );
          return;
        }
      }

      let { error } = await setNewPassword(
        email,
        state.password,
        state.newPassword,
        props.errorHandler
      );
      // If no error, go to next state
      if (!error) {
        setError(false);
        setLoading(false);
        setCompleted(true);
        return;
      } else {
        setError(true);
        setLoading(false);
        setErrorMessage(error.error);
        return;
      }
    } catch (e) {
      console.log('Network error:', e);
      setError(true);
      setLoading(false);
      setErrorMessage('Network Error');
    }
  };

  const generateFormError = () => {
    if (!hasError) return null;
    return <div className="text-danger">{errorMessage}</div>;
  };

  /**
   * Goes back to the login page.
   */
  const backToLogin = () => {
    props.navigate('/');
  };

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const generatePage = () => {
    // If the application is currently loading, show Loader
    if (isLoading) {
      return <Loader />;
    }
    // Registration state is completed
    else if (isCompleted) {
      //todo add page navigation + login
      handleLogin(email, state.password);
      return (
        <Typography variant="h5">
          {PasswordRecoveryFormattedMessages.successfulResetLogin}
        </Typography>
      );
    }
    // if valid url and no longer loading, show form
    else if (validUrl && !isLoading) {
      return (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack gap={2}>
            <TextField
              id="password"
              size="small"
              label={PasswordRecoveryFormattedMessages.newPassword}
              variant="outlined"
              fullWidth
              type={state.showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              error={state.password !== '' && !validPassword}
              helperText={
                state.password !== '' && !validPassword
                  ? 'Password is not strong enough.'
                  : ''
              }
              onChange={handleChange}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {state.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                },
              }}
            />

            <TextField
              id="newPassword"
              size="small"
              label={PasswordRecoveryFormattedMessages.confirmNewPassword}
              variant="outlined"
              fullWidth
              type={state.showPassword ? 'text' : 'password'}
              name="newPassword"
              autoComplete="new-password"
              error={state.password !== '' && !validConfirmPassword}
              helperText={
                state.password !== '' && !validConfirmPassword
                  ? "Passwords don't match."
                  : ''
              }
              onChange={handleChange}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {state.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                },
              }}
            />

            <PasswordRequirements />

            {generateFormError()}

            <LoaderButton
              fullWidth
              disabled={!validateForm()}
              type="submit"
              isLoading={isLoading}
              text={'Set new password'}
              loadingText={'Loading'}
              onClick={(event) => handleSubmit(event)}
            />
          </Stack>
        </Box>
      );
    } else if (!validUrl && hasError) {
      return <Alert severity="error">{errorMessage}</Alert>;
    }
  };

  // render return
  return (
    <FullscreenLayout fullHeight>
      <Stack
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          gap: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'common.white',
            borderRadius: 4,
            paddingTop: 2,
            paddingBottom: 4,
            paddingX: { xs: 2, md: 4 },
            width: 400,
            maxWidth: '100%',
          }}
        >
          <BackButton
            onClick={backToLogin}
            backButtonText={PasswordRecoveryFormattedMessages.backToLogin}
          />

          <Box
            sx={{
              marginBottom: 4,
              marginTop: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src={CompleteSMSLogo}
              alt={`CompleteSMS Logo ${process.env.REACT_APP_COLOUR}`}
              width={200}
            />
          </Box>

          {generatePage()}
        </Box>
      </Stack>
    </FullscreenLayout>
  );
};

export default NewPassword;
